import { IProperty, IPropertyWithType, PropertyMetadata } from "./properties.js";


// All vector/color ducks require NUMERIC components: `'x' in obj` walks the
// prototype chain, and TSL nodes expose swizzle GETTERS (.x/.y/.z/.w and
// .r/.g/.b/.a return nodes) on EVERY node — float uniforms classified as
// Vector4/Color and rendered with garbage component editors.

export type Vec2Like = { x: number; y: number; };
export function isVec2Like(obj: IProperty): obj is IPropertyWithType<Vec2Like> {
    const val = obj.value as { x?: unknown; y?: unknown } | null;
    if (!val) return false;
    if (typeof val !== "object") return false;
    return typeof val.x === "number" && typeof val.y === "number";
}

export type Vec3Like = { x: number; y: number; z: number; };
export function isVec3Like(obj: IProperty): obj is IPropertyWithType<Vec3Like> {
    const val = obj.value as { x?: unknown; y?: unknown; z?: unknown } | null;
    if (!val) return false;
    if (typeof val !== "object") return false;
    return typeof val.x === "number" && typeof val.y === "number" && typeof val.z === "number";
}


export type Vec4Like = { x: number; y: number; z: number; w: number; };
export function isVec4Like(obj: IProperty): obj is IPropertyWithType<Vec4Like> {
    const val = obj.value as { x?: unknown; y?: unknown; z?: unknown; w?: unknown } | null;
    if (!val) return false;
    if (typeof val !== "object") return false;
    return typeof val.x === "number" && typeof val.y === "number" && typeof val.z === "number" && typeof val.w === "number";
}

export function isVectorPropertyLike(obj: IProperty): obj is IPropertyWithType<Vec2Like | Vec3Like | Vec4Like> {
    return isVec2Like(obj) || isVec3Like(obj) || isVec4Like(obj);
}
export function isVectorLike(obj: any): obj is Vec2Like | Vec3Like | Vec4Like {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    return typeof obj.x === "number" && typeof obj.y === "number";
}

export type MatrixLike = { elements: ArrayLike<number> };

export function hasElementsArray(obj: unknown): obj is MatrixLike {
    return !!obj
        && typeof obj === "object"
        && "elements" in obj
        && !!(obj as any).elements
        && typeof (obj as any).elements.length === "number";
}

export function getMatrixDimension(obj: unknown): 2 | 3 | 4 | undefined {
    if (!hasElementsArray(obj)) return undefined;
    switch (obj.elements.length) {
        case 4: return 2;
        case 9: return 3;
        case 16: return 4;
        default: return undefined;
    }
}

export function isMatrixLike(obj: any): obj is MatrixLike {
    return getMatrixDimension(obj) !== undefined;
}

export function isMatrixPropertyLike(obj: IProperty): obj is IPropertyWithType<MatrixLike> {
    return isMatrixLike(obj.value);
}

export function isIdentityMatrixElements(elements: ArrayLike<number>, dimension: number): boolean {
    const expectedLength = dimension * dimension;
    if (elements.length !== expectedLength) return false;
    for (let row = 0; row < dimension; row++) {
        for (let col = 0; col < dimension; col++) {
            const index = col * dimension + row;
            const expected = row === col ? 1 : 0;
            if (elements[index] !== expected) {
                return false;
            }
        }
    }
    return true;
}

export type RGBColorLike = { r: number; g: number; b: number; };
export function isRGBColorPropertyLike(obj: IProperty): obj is IPropertyWithType<RGBColorLike> {
    const val = obj.value;
    return isRGBColorLike(val);
}
export function isRGBColorLike(obj: any): obj is RGBColorLike {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    // NUMERIC r/g/b — `'r' in obj` walks the prototype chain, and TSL nodes
    // expose swizzle GETTERS (.r/.g/.b return nodes) on every node, which
    // classified float uniforms as colors (black swatches in the inspector).
    return typeof obj.r === "number" && typeof obj.g === "number" && typeof obj.b === "number";
}

export type RGBAColorLike = { r: number; g: number; b: number; a: number; };
export function isRGBAColorPropertyLike(obj: IProperty): obj is IPropertyWithType<RGBAColorLike> {
    const val = obj.value;
    return isRGBAColorLike(val);
}
export function isRGBAColorLike(obj: any): obj is RGBAColorLike {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    // numeric check — see isRGBColorLike (TSL swizzle getters)
    return typeof obj.r === "number" && typeof obj.g === "number" && typeof obj.b === "number" && typeof obj.a === "number";
}

export type ColorLike = RGBColorLike | RGBAColorLike;
export function isColorPropertyLike(obj: IProperty): obj is IPropertyWithType<ColorLike> {
    return isRGBColorPropertyLike(obj) || isRGBAColorPropertyLike(obj);
}
export function isColorLike(obj: any): obj is ColorLike {
    return isRGBColorLike(obj) || isRGBAColorLike(obj);
}

export type ThreeObjectLike = import("three").Object3D;
export function isThreeObjectLike(obj: IProperty): obj is IPropertyWithType<import("three").Object3D> {
    const val = obj.value;
    if (!val) return false;
    if (typeof val !== "object") return false;
    return 'uuid' in val && 'type' in val && 'name' in val && 'children' in val && 'matrix' in val && 'position' in val && 'quaternion' in val && 'scale' in val;
}
export function isTextureLike(obj: any): obj is import("three").Texture {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    return 'uuid' in obj && 'image' in obj && 'wrapS' in obj && 'wrapT' in obj && 'magFilter' in obj && 'minFilter' in obj;
}

export function isTexturePropertyLike(obj: IProperty): obj is IPropertyWithType<import("three").Texture | null> {
    const val = obj.value;
    if (!val) return false;
    if (typeof val !== "object") return false;
    return isTextureLike(val);
}


// #region Test property accept types
export function propertyAcceptsNull(obj: IProperty): boolean | undefined {
    return PropertyMetadata.testAccept(obj, "null");
}
export function propertyAcceptsImage(obj: IProperty): boolean | undefined {
    return PropertyMetadata.testAccept(obj, "image/*");
}
export function propertyAcceptsColor(obj: IProperty): boolean | undefined {
    return PropertyMetadata.testAccept(obj, "color/*");
}
